import React from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Text } from 'tamagui';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { useTranslation } from 'react-i18next';

type EmailSignUpButtonProps = {
  onPress: () => void;
};

/**
 * OAuth-style pill button for "Sign up with Email" — matches GoogleButtonLogin / AppleButton layout.
 */
const EmailSignUpButton = ({ onPress }: EmailSignUpButtonProps) => {
  const { t } = useTranslation();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const buttonColors = {
    background: theme ? '#ffffff' : 'rgba(30, 41, 59, 0.95)',
    text: theme ? '#111827' : '#e2e8f0',
    border: theme ? '#e5e7eb' : 'rgba(148, 163, 184, 0.35)',
    hover: theme ? 'rgba(90,192,234,0.25)' : 'rgba(59,130,246,0.2)',
    shadow: theme ? '#000000' : '#020617',
    icon: theme ? '#EA4335' : '#fca5a5',
  };

  const outerStyle = {
    minHeight: Platform.select({ web: 50, default: 50 }),
    height: 50,
    maxHeight: 50,
    width: '100%' as const,
    maxWidth: Platform.OS === 'web' ? 400 : undefined,
    alignSelf: Platform.OS === 'web' ? ('center' as const) : undefined,
    flex: Platform.OS === 'web' ? 0 : 1,
    elevation: 1,
    shadowRadius: 10,
    shadowOpacity: 0.2,
    backgroundColor: buttonColors.background,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: buttonColors.border,
    shadowColor: buttonColors.shadow,
    flexDirection: 'row' as const,
    overflow: 'hidden' as const,
    justifyContent: 'center' as const,
    alignContent: 'center' as const,
    alignItems: 'stretch' as const,
  };

  return (
    <Pressable onPress={onPress} style={outerStyle}>
      {({ hovered }) => (
        <View style={{ flex: 1, width: '100%' }}>
          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? buttonColors.hover : 'rgba(90,192,234,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <FontAwesome name="envelope" size={20} color={buttonColors.icon} />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: Platform.OS === 'web' ? 0 : 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{
                  fontSize: responsiveFontSize(15),
                  color: buttonColors.text,
                  fontWeight: '600',
                }}
              >
                {t('Sign up with Email')}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
};

export default EmailSignUpButton;
